<script setup>
import { nextTick, onMounted, ref } from "vue";
import GroupDevice from "@/views/components/GroupDevice.vue";
import { noticeList, noticeDetail } from "@/api/alarmNotice";
import { Plus, Search } from "@element-plus/icons-vue";
import { parseTime } from "@/utils/ruoyi";
import { signData, signTimeDataMap, deviceTsl } from "@/api/device";
import * as echarts from "echarts";

import { useI18n } from "vue-i18n";

// 引入i18n用于国际化
const { t } = useI18n();


// 告警通知列表数据
const noticeListData = ref([]);

// 告警通知查询参数
const noticeParams = ref({
  pageSize: 10,
  pageNo: 1,
  noticeParams: '',
  title: ''
});

const background = ref(true)
const disabled = ref(false)

const echartContainer = `notice-${Date.now()}`


// 当前选中的告警通知
const currentNotice = ref(null);

// 设备标识数据
const signDataObj = ref(null);

// 获取设备标识数据
const getSignData = (list) => {
  if (Array.isArray(list)) {
    signData(list).then(res => {
      signDataObj.value = res.data;
    });
  } else {
    console.error('list 不是数组');
  }
};

// ECharts 容器引用
const noticeContainer = ref(null);
const identifierMap = ref(null)
// 获取折线图数据
const getLineData = async (params) => {
  let myChart = echarts.init(document.getElementById(echartContainer));
  try {
    const res = await signTimeDataMap(params);
    if (res && res.data && currentTsl.value) {

      // 解析通知数据并生成identifier到functionName的映射
      identifierMap.value = currentTsl.value.reduce((map, prop) => {
        map[prop.identifier] = prop.functionName;
        return map;
      }, {});

      // 替换数据中的属性标识
      res.data = Object.fromEntries(
          Object.entries(res.data).map(([key, value]) => {
            const newValue = Object.fromEntries(
                Object.entries(value).map(([attr, attrValue]) => {
                  const newAttr = identifierMap.value[attr] || attr;
                  return [newAttr, attrValue];
                })
            );
            return [key, newValue];
          })
      );

      let option = {
        xAxis: {
          type: 'category',
          data: []
        },
        legend: {
          data: []
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '16%',
          containLabel: true
        },
        yAxis: {
          type: 'value'
        },
        series: [
          {
            data: [],
            type: 'line',
            // smooth: true
          }
        ],
      }

      // 设置X轴时间和数据
      option.xAxis.data = Object.keys(res.data).map(timestamp => parseTime(timestamp));
      const firstObject = Object.values(res.data)[0];
      const attributes = Object.keys(firstObject).filter(key => key !== 'azimuth');

      option.legend.data = attributes;

      option.legend.data = attributes.map(attribute => {
        return t(`element.${attribute}`);
      });


      option.series = attributes.map(attr => ({
        name: t(`element.${attr}`),
        type: 'line',
        data: Object.values(res.data).map(record => record[attr].value)
      }));

      // 更新图表
      if (option) {
        myChart.setOption(option);
      }
    }
  } catch (error) {
    console.error("获取折线图数据时出错:", error);
  }

};

// 当前设备属性标识
const currentTsl = ref(null);

// 获取设备属性标识信息
const getTsl = (params) => {
  deviceTsl(params).then(res => {
    if (res && res.data && res.data.propertie) {
      currentTsl.value = res.data.propertie;

      console.log(' = currentTsl currentTsl', currentTsl.value)

      // 获取告警折线数据
      const lineParams = {
        deviceName: currentNotice.value.alarmDeviceInfoRespDTO.deviceName,
        startTime: currentNotice.value.alarmNotice.startTime,
        endTime: currentNotice.value.alarmNotice.endTime,
        expansionTime: 5
      };
      getLineData(lineParams);
    }
  });
};

// 属性标识列表
const signList = ref([]);

// 选择告警通知项
const selectedNovice = (item, index) => {
  if (!item.isSelect) {
    noticeDetail({ id: item.id }).then(res => {
      currentNotice.value = res.data;

      if (res && res.data && res.data.alarmRules && res.data.alarmRules.conditions) {
        signList.value = res.data.alarmRules.conditions.map(item => item.tslAttribute);
        getTsl({ deviceName: res.data.alarmDeviceInfoRespDTO.deviceName });
        getSignData(signList.value);
      }
    });

    // 更新选择状态
    noticeListData.value = noticeListData.value.map((it, idx) => {
      it.isSelect = index === idx;
      return it;
    });
  }
};


// 告警通知总数
const total = ref(null);

// 获取告警通知列表
const getNoticeList = (params) => {
  noticeList(params).then(res => {
    if (res && res.data && res.data.list && res.data.list.length > 0) {
      total.value = res.data.total;
      res.data.list.forEach(item => item.isSelect = false);
      res.data.list[0].isSelect = true;

      noticeDetail({ id: res.data.list[0].id }).then(res => {
        currentNotice.value = res.data;
        if (res && res.data && res.data.alarmRules && res.data.alarmRules.conditions) {
          signList.value = res.data.alarmRules.conditions.map(item => item.tslAttribute);
          getTsl({ deviceName: res.data.alarmDeviceInfoRespDTO.deviceName });
          getSignData(signList.value);
        } else {
          console.warn('暂无条件信息');
        }
      });

      noticeListData.value = res.data.list;
    } else {
      noticeListData.value = [];
      signDataObj.value = null;
      currentNotice.value = null;
    }
  });
};

// 处理设备列表选择事件
const deviceList = (e) => {
  if(e && e.length === 0){
    noticeParams.value.deviceNameStr = null;
    getNoticeList(noticeParams.value);
  } else {
    noticeParams.value.deviceNameStr = e.join(',');
    getNoticeList(noticeParams.value);
  }


};

// 处理告警通知选择事件
const selectNovice = () => {
  getNoticeList(noticeParams.value);
};

// 解决状态映射对象
const resolveStatusObj = {
  0: 'inform.pending',
  1: 'inform.automaticResolution',
  2: 'inform.manualSolution'
};

// 告警级别映射对象
const alarmLevelObj = {
  0: 'alarm.general',
  1: 'alarm.significance',
  2: 'alarm.seriousness',
};

// 处理分页大小变化事件
const handleSizeChange = (val) => {
  noticeParams.value.pageSize = val;
  getNoticeList(noticeParams.value);
};

// 处理当前页变化事件
const handleCurrentChange = (val) => {
  noticeParams.value.pageNo = val;
  getNoticeList(noticeParams.value);
};

// 组件挂载时执行
onMounted(() => {
  getNoticeList(noticeParams.value);
});

// 毫秒转化为分，秒
const convertMilliseconds = (durationTime) => {
  const minutes = Math.floor(durationTime / 60000);
  const seconds = Math.floor((durationTime % 60000) / 1000);

  return [minutes, seconds];
}

</script>

<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-lg-9 col-12">
        <div class="card h-100">
          <div class="card-header">
            <h5>{{$t('routesMenu.inform')}}</h5>
            <div class="d-flex align-items-center">
              <el-input
                  v-model="noticeParams.title"
                  style="width: 180px;"
                  :placeholder="$t('alarm.searchTitle')"
                  :prefix-icon="Search"
              />
              <el-button :icon="Search" type="primary" class="m-lg-1" @click="selectNovice">{{$t('arg.query')}}</el-button>

            </div>
          </div>
          <div class="card-body" style="height: 75vh; overflow: auto;">
            <div v-if="noticeListData && noticeListData.length > 0" class="row d-flex">
              <div class="col-5" style="height: 70vh; overflow: scroll;">
                <div v-if="noticeListData && noticeListData.length > 0">
                  <div v-for="(item,index) in noticeListData" :key="index" @click="selectedNovice(item,index)">
                    <div class="card-bg" :style="{'border': item.isSelect ? '1px solid #099B5C' : ''}">
                      <div class="d-flex align-items-center justify-content-between p-2">
                        <p class="d-flex align-items-center" >{{item.title}}</p>
                        <p class="d-flex align-items-center" >
                          <el-tag v-if="item.resolveStatus === 0" type="danger">{{$t(resolveStatusObj[item.resolveStatus])}}</el-tag>
                          <el-tag v-if="item.resolveStatus === 1" type="success">{{$t(resolveStatusObj[item.resolveStatus])}}</el-tag>
                          <el-tag v-if="item.resolveStatus === 2" type="primary">{{$t(resolveStatusObj[item.resolveStatus])}}</el-tag>
                        </p>

                      </div>
                      <div class="p-2">

                        <el-tag v-if="item.alarmLevel === 0" type="primary">{{$t(alarmLevelObj[item.alarmLevel])}}</el-tag>
                        <el-tag v-if="item.alarmLevel === 1" type="warning">{{$t(alarmLevelObj[item.alarmLevel])}}</el-tag>
                        <el-tag v-if="item.alarmLevel === 2" type="danger">{{$t(alarmLevelObj[item.alarmLevel])}}</el-tag>
                      </div>
                      <div class="p-2">
                        {{parseTime(item.createTime)}}
                      </div>
                    </div>
                  </div>
                </div>
                <el-empty v-else :description="$t('home.noData')" />

              </div>
              <div class="col-7 p-3 card-bg">
                <div v-if="currentNotice">
                  <div class=" d-flex align-items-center justify-content-between">
                    <!--                  {{currentNotice}}-->

                    <div class="d-flex align-items-center p-2" v-if="currentNotice && currentNotice.alarmNotice">
                      <div class="d-flex font-weight-bold">{{currentNotice.alarmNotice.title}}</div>
                      <el-tag v-if="currentNotice.alarmNotice.resolveStatus === 0" type="primary">{{$t(resolveStatusObj[currentNotice.alarmNotice.resolveStatus])}}</el-tag>
                      <el-tag v-if="currentNotice.alarmNotice.resolveStatus === 1" type="primary">{{$t(resolveStatusObj[currentNotice.alarmNotice.resolveStatus])}}</el-tag>
                      <el-tag v-if="currentNotice.alarmNotice.resolveStatus === 2" type="primary">{{$t(resolveStatusObj[currentNotice.alarmNotice.resolveStatus])}}</el-tag>
                    </div>
                    <div class="d-flex align-items-center p-2" v-if="currentNotice && currentNotice.alarmNotice">
                      <el-tag v-if="currentNotice.alarmNotice.alarmLevel === 0" type="primary">{{$t(alarmLevelObj[currentNotice.alarmNotice.alarmLevel])}}</el-tag>
                      <el-tag v-if="currentNotice.alarmNotice.alarmLevel === 1" type="warning">{{$t(alarmLevelObj[currentNotice.alarmNotice.alarmLevel])}}</el-tag>
                      <el-tag v-if="currentNotice.alarmNotice.alarmLevel === 2" type="danger">{{$t(alarmLevelObj[currentNotice.alarmNotice.alarmLevel])}}</el-tag>
                    </div>

                  </div>
                  <div class="p-2 font-weight-bold" v-if="currentNotice && currentNotice.alarmNotice">{{currentNotice.alarmNotice.alarmDesc}}</div>
                  <div class="p-2" v-if="currentNotice && currentNotice.alarmRules && currentNotice.alarmRules.conditions" ref="noticeContainer" :id="echartContainer" style="width: 100%; height: 300px;">
                  </div>
                  <el-empty v-else :description="$t('home.noData')" />

                  <!-- 告警规则 -->
                  <div class="p-4" style="background-color: #fff;" v-if="currentNotice && currentNotice.alarmRules">
                    <el-descriptions :title="$t('inform.warningMessage')" :column="2">
                      <el-descriptions-item :label="$t('alarm.alarmCondition')">
                        <el-tag v-for="(item,index) in currentNotice.alarmRules.conditions" :key="index">
                          <div v-if="identifierMap && identifierMap[item.tslAttribute] && identifierMap[item.tslAttribute]">
                            {{identifierMap[item.tslAttribute]}} {{item.subconditions}} {{item.value}}
                          </div>
                          <div v-else>
                            {{item.tslAttribute}} {{item.subconditions}} {{item.value}}
                          </div>
                        </el-tag>
                      </el-descriptions-item>
                      <el-descriptions-item :label="$t('alarm.startTime')">{{parseTime(currentNotice.alarmNotice.startTime) || '--'}}</el-descriptions-item>
                      <el-descriptions-item :label="$t('alarm.endTime')">{{parseTime(currentNotice.alarmNotice.endTime) || '--'}}</el-descriptions-item>
                      <el-descriptions-item :label="$t('alarm.duration')">{{convertMilliseconds(currentNotice.alarmNotice.durationTime)[0] }} {{$t('alarm.minute')}} {{convertMilliseconds(currentNotice.alarmNotice.durationTime)[1]}} {{$t('alarm.second')}}</el-descriptions-item>
                    </el-descriptions>
                  </div>
                  <div class="p-4" style="background-color: #fff; margin-top: 8px;" v-if="currentNotice && currentNotice.alarmDeviceInfoRespDTO">
                    <el-descriptions :title="$t('home.deviceInfo')" :column="2">
                      <el-descriptions-item :label="$t('home.deviceName')">{{currentNotice.alarmDeviceInfoRespDTO.deviceName || '--'}}</el-descriptions-item>
                      <el-descriptions-item :label="$t('home.onlineState')">
                        <el-tag type="danger" v-if="currentNotice.alarmDeviceInfoRespDTO.onlineState === 0">{{$t('home.offLine')}}</el-tag>
                        <el-tag type="success" v-if="currentNotice.alarmDeviceInfoRespDTO.onlineState === 1">{{$t('home.onLine')}}</el-tag>
                      </el-descriptions-item>
                    </el-descriptions>
                  </div>
                </div>
                <el-empty v-else :description="$t('home.noData')" />

              </div>
            </div>
            <el-empty v-else :description="$t('home.noData')" />

            <div v-if="noticeListData && noticeListData.length > 0" class="p-3 d-flex align-items-center justify-content-end">
              <el-pagination
                  v-model:current-page="noticeParams.pageNo"
                  v-model:page-size="noticeParams.pageSize"
                  :page-sizes="[10, 100, 200, 300, 400]"
                  :disabled="disabled"
                  :background="background"
                  layout="sizes, prev, pager, next"
                  :total="total"
                  @size-change="handleSizeChange"
                  @current-change="handleCurrentChange"
              />
            </div>

          </div>
        </div>
      </div>

      <div class="col-lg-3 col-12 mt-2">
        <div class="card h-100">
          <div class="card-header">
            <h5>{{$t('home.deviceList')}}</h5>
          </div>
          <div class="card-body">
            <group-device @device-list="deviceList"></group-device>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.card-bg {
  background-color: #F7F8FC;
  margin-bottom: 10px;
}
.card-bg:hover {
  border: 1px solid #099B5C;
}
</style>
